import React, { useState, useEffect, useMemo } from "react";
import * as am4core from "@amcharts/amcharts4/core";
import {
	IOverviewChannel,
	IChannelTS,
	IChannelFC,
	getChannelTS
} from "../../../services/api/Dashboard";
import Year, { IYear } from "../../../services/api/Year";
import Month, { IMonth } from "../../../services/api/Month";
import ChannelType, { IChannelType } from "../../../services/api/ChannelType";
import Select from "../../../components/Form/Select";
import Donut from "./charts/Donut";
import Waterfall from "./charts/Waterfall";
import Loading from "../../../components/Loading";
import Formatter from "../../../services/utils/Formatter";
import { getOverviewChannels } from "./channelCache";

interface IChannelOverviewProps {

}


const ChannelOverview: React.FC<IChannelOverviewProps> = () => {
	const [loading, isLoading] = useState<Boolean>(true);
	const [years, setYears] = useState<IYear[]>([]);
	const [months, setMonths] = useState<IMonth[]>([]);
	const [channelTypes, setChannelTypes] = useState<IChannelType[]>([]);
	const [current, setOverview] = useState<IOverviewChannel>({} as IOverviewChannel);
	const [monthOverview, setMonthOverview] = useState<IOverviewChannel | null>(null);
	const [isYTD, setIsYTD] = useState(false);

	const [year, setYear] = useState<number>(0);
	const [month, setMonth] = useState<number>(0);
	const [channelType, setChannelType] = useState<string>("segment");
	const [fType, setFType] = useState<string | null>();
	const [fVs, setFVs] = useState<string>(handleDefaultReference(channelType));
	const [fPs, setFPs] = useState<string>("MONTH");
	const graphColors: string[] = [
		"#f91054", "#fcc003", "#00162b", "#656061", 
		"#B26B78", "#F4801F", "#2D5072",
		"#d94063", "#ffbc03", "#00335e", "#9d8e85",
		"#c9a1b4", "#fb7c1b", "#4f7690",
		"#f9c6d3", "#f2e3c9", "#8e9a99", "#f9a3c1",
		"#ff934b", "#5a728b"
	  ];

	const date = new Date();
	// Se o mês for janeiro (0), ajusta para dezembro do ano anterior, se não, subtrai 1 mês.

	if (date.getMonth() === 0) {
		date.setFullYear(date.getFullYear() - 1);  // Ajusta o ano para o anterior
		date.setMonth(11);  // Define o mês como dezembro (11)
	} else {
		date.setMonth(date.getMonth() - 1);
	}

	let mapData: object[] = [];
	let valueTSTotal: number = 0;
	let valueMapTotal: number = 0;

	const countries: Array<IOverviewChannel> = [];
	const majors: Array<IOverviewChannel> = [];
	let all: Array<IOverviewChannel> = [];
	let allMonth: Array<IOverviewChannel> = [];

	function handleChannelText(channelType: string, capitalize: boolean = false) {
		if (channelType === "segment")
			return capitalize ? "Segment" : "segment";

		return capitalize ? "Source" : "source";
	}

	async function LoadOverview(year: number, month: number) {
		isLoading(true);

		const response = await getOverviewChannels(year, month, channelType);
		const { overview, ytdOverview } = response;
		setIsYTD(fPs !== "YTD");
		setOverview(isYTD ? ytdOverview : overview);
		setMonthOverview(isYTD ? overview : null);
		isLoading(false);
	}

	useEffect(() => {
		Promise.all([Year.GetAll(), Month.GetAll(), ChannelType.GetAll()])
			.then(async (value) => {
				const years = value[0].filter((value) => { return value.active });
				setYears(years);
				setMonths(value[1]);
				setChannelTypes(value[2]);

				setYear(date.getFullYear());
				setMonth(date.getMonth() + 1);
				setChannelType(channelTypes[0]?.slug ?? "segment");

				await LoadOverview(years[0].name, date.getMonth() + 1);
			})
			.catch(() => undefined);
	}, []);

	useEffect(() => {
		const loadOverviewAsync = async () => {
			if (!loading)
				await LoadOverview(year, month);
		};

		loadOverviewAsync();
		setFVs(handleDefaultReference(channelType));

	}, [year, month, fPs, channelType, channelTypes]);

	function handleDefaultReference(channelType: string) {
		return "BP";
		//return channelType === "source" ? "FRE" : "BP"
	}

	function mapList(obj: any): any {
		return { value: obj.id, label: obj.name };
	}

	function mapChannelTypeList(obj: any): any {
		return { value: obj.slug, label: obj.name };
	}

	function filterContent(obj: IOverviewChannel, isYTD: boolean = false): void {
		if (isYTD) {
			// Primeiro processa o objeto atual antes de descer para os filhos
			if (!obj.featured) {
				allMonth.push(obj);
			}
			
			// Depois processa os filhos recursivamente
			if (Array.isArray(obj.children)) {
				obj.children.forEach(child => filterContent(child, true));
			}

		} else {
			// Processa o objeto atual
			if (obj.longitude !== 0 && obj.latitude !== 0) {
				countries.push(obj);
			}
			
			(obj.featured ? majors : all).push(obj);
			
			// Depois processa os filhos recursivamente
			if (Array.isArray(obj.children)) {
				obj.children.forEach(child => filterContent(child));
			}
		}
	}


	if (!loading) {
		filterContent(current);

		const index = all.findIndex(item => item.code === 'LATAM');
		if (index !== -1) 
			all.splice(index, 1);

		if (monthOverview && isYTD) {
			filterContent(monthOverview, true);
		}

		countries.forEach((obj: IOverviewChannel, i: number) => {

			let valueBaseTS: number = 0;
			let valueBaseMap: number = 0;

			const fsChannels: IChannelFC[] = obj.actualChannels;
			const tsChannels: IChannelTS[] = obj.planChannels;

			let currentValue: number = 0;
			let monthChild: IOverviewChannel | undefined = undefined;
			if (monthOverview) {
				monthChild = monthOverview.children.find((child: IOverviewChannel) => child.code === obj.code);
				if (!monthChild) {
					const casaChild = monthOverview.children.find((child: IOverviewChannel) => child.code === 'CASA');
					if (casaChild)
						monthChild = casaChild.children.find((child: IOverviewChannel) => child.code === obj.code);
				}
			}

			(tsChannels || []).forEach((children: IChannelTS, j: number) => {
				if (fType === children.name || fType === undefined) {

					let channel: IChannelFC = fsChannels[j];

					let monthFS = {} as IChannelFC;
					let monthTS = {} as IChannelTS;

					if (isYTD && monthChild) {
						monthFS = monthChild.actualChannels.find((child: IChannelFC) => child.name === children.name) as IChannelFC;
						monthTS = monthChild.planChannels.find((child: IChannelTS) => child.name === children.name) as IChannelTS;
						currentValue += monthFS?.actual ?? 0;

					} else {
						valueBaseTS += channel?.actual ?? 0;
					}

					if (fVs === "BP") {
						valueBaseMap += children.planBusinessPlan;
						if (isYTD)
							valueBaseMap += monthTS?.planBusinessPlan ?? 0;
					}
					else if (fVs === "FRE") {
						valueBaseMap += children.planFRE;
						if (isYTD)
							valueBaseMap += monthTS?.planFRE ?? 0;
					}
					else if (fVs === "SRE") {
						if (isYTD)
							valueBaseMap += children.planSRE;
					}
					else { // LY
						if (obj.lastYear?.actualChannels && obj.lastYear.actualChannels.length > 0)
							valueBaseMap += obj.lastYear.actualChannels[j]?.actual ?? 0;
					}
				}
			});

			valueBaseTS += currentValue;

			valueTSTotal += valueBaseTS;
			valueMapTotal += valueBaseMap;

			mapData.push({
				title: obj.name,
				latitude: obj.latitude,
				longitude: obj.longitude,
				value: valueBaseTS,
				size: 0
			});
		});

		//Populate Map
		(mapData || []).forEach((obj: any, i: number) => {
			let value = Math.max(2, Math.abs(100 * obj.value) / (valueMapTotal == 0 ? valueTSTotal : valueMapTotal));
			obj.size = Number.isNaN(value) ? 1 : value;
		});
	}

	const graphData = useMemo(() => {
		let LFC = 0;
		let FC = 0;
		const graphData: any = [];

		const fsChannels: IChannelFC[] = current?.actualChannels;
		const tsChannels: IChannelTS[] = current?.planChannels;

		if (!fsChannels || !tsChannels) return graphData;

		fsChannels.forEach((obj: IChannelFC, index: number) => {
			let fcv = obj.actual;
			let lfcv = 0;

			if (fVs === "BP") {
				if (tsChannels[index] !== undefined)
					lfcv += tsChannels[index].planBusinessPlan;
			} else if (fVs === "FRE") {
				if (tsChannels[index] !== undefined)
					lfcv = tsChannels[index].planFRE;
			} else if (fVs === "SRE") {
				if (tsChannels[index] !== undefined)
					lfcv = tsChannels[index].planSRE;
			} else { // LY
				if (current?.lastYear?.actualChannels && current?.lastYear?.actualChannels[index])
					lfcv = current.lastYear.actualChannels[index].actual;
			}
			graphData.push({
				category: obj.name,
				value: fcv - lfcv
			});

			LFC += lfcv;
			FC += fcv;
		});
		
		graphData.splice(0, 0, {
			category: fVs,
			value: LFC
		});

		graphData.push({
			category: "ACT",
			value: FC
		});

		return graphData;
	}, [current, fVs]);

	function getTSTypes() {

		return [
			{ value: "BP", label: "BP" },
			{ value: "FRE", label: "FRE" },
			{ value: "SRE", label: "SRE" },
		];


	}



	function toggleChildren(e: any) {
		e.target.classList.toggle("childrenButton--open");
		var isOpen = e.target.classList.contains("childrenButton--open");
		//hide or show row
		document.querySelectorAll("[class^='childrenRow " + e.target.dataset.rowClass + "']").forEach(element => {

			//close all descendant buttons
			element.querySelectorAll(".childrenButton").forEach(bt => { bt.classList.remove("childrenButton--open") });

			if (isOpen) {
				//show only imediate children
				if (element.classList.contains(e.target.dataset.rowClass)) {
					element.classList.add("childrenRow--hidden");
					element.classList.remove("childrenRow--open")
				}
			}
			else {
				//close all descendants
				element.classList.remove("childrenRow--hidden");
				element.classList.add("childrenRow--open");
			}

		});
	}

	const index = all.findIndex(item => item.code === 'LATAM');
	let latam;
	if (index !== -1) 
		latam = all.splice(index, 1);

	const allArray = all.filter(x => x.name !== 'All Others').map((obj: IOverviewChannel, i: number) => { 
		//#region NEW CODE
		let currentValue: number = 0;
		let monthChild: IOverviewChannel | undefined = undefined;
		if (monthOverview) {
			if (obj.code === 'LATAM') {
				monthChild = monthOverview;
			} else {
				const casa = monthOverview.children.find((child: IOverviewChannel) => child.code === 'CASA');
				if (casa)
					monthChild = allMonth.find((child: IOverviewChannel) => child.code === obj.code);
			}
		}
		//#endregion

		let valueBase: number = 0;
		let valueBaseYTD: number = 0;
		let valueLY: number = 0;
		let valueTS: number = 0;

		const fsChannels: IChannelFC[] = obj.actualChannels;
		const tsChannels: IChannelTS[] = obj.planChannels;

		tsChannels.forEach(
			(children: IChannelTS, j: number) => {
				if (
					fType === children.name ||
					fType === undefined
				) {
					valueBase += getChannelTS(children, fVs);

					let channel: IChannelFC = fsChannels[j];

					if (monthChild && isYTD) {
						let monthFS = monthChild.actualChannels.find((child: IChannelFC) => child.name === children.name) as IChannelFC;
						let monthTS = monthChild.planChannels.find((child: IChannelTS) => child.name === children.name) as IChannelTS;

						currentValue += monthFS?.actual ?? 0;

						valueBaseYTD += getMonthTSValue(monthTS, fVs);
					} else {
						valueTS += channel?.actual ?? 0;
					}

					if (obj.lastYear?.actualChannels && obj.lastYear.actualChannels.length > 0) {
						const lyChannel = obj.lastYear.actualChannels.find((fc: IChannelFC) => fc.name === children.name);
						valueLY += lyChannel?.actual ?? 0;
					}
				}
			}
		);

		valueTS += currentValue;
		valueBase += valueBaseYTD;
		
		if(obj.children == null) {
			return (
				<tr key={"countryData" + obj.name + obj.id} className={(`childrenRow childrenRow-${obj.parentId}`)}>
					<td>{obj.name}</td>
					<td>{Formatter.DisplayValue(valueTS)}</td>
					<td>{Formatter.DisplayPercent((valueTS / valueLY - 1) * 100)}</td>
					<td className="table__data--border">{Formatter.DisplayPercent((valueTS / valueBase - 1) * 100)}</td>
				</tr>
			);
		}

		const rowClass = obj.code == 'LATAM' 
			? '' 
			: `childrenRow childrenRow-${obj.parentId}`;
		
		return (
			<tr key={"countryDataRow" + obj.name + obj.id} className={rowClass}>
				{obj.code !== 'LATAM'
				? <td>				
					<a key={"countryDataLink" + obj.name + obj.id} className="childrenButton" data-row-class={`childrenRow-${obj.id}`} onClick={toggleChildren} title="Toggle">
					{obj.name}
					</a>
				</td>
				: <td>{obj.name}</td>}
				<td>{Formatter.DisplayValue(valueTS)}</td>
				<td>{Formatter.DisplayPercent((valueTS / valueLY - 1) * 100)}</td>
				<td className="table__data--border">{Formatter.DisplayPercent((valueTS / valueBase - 1) * 100)}</td>
			</tr>
		);

	})

	if(latam)
		majors.unshift(latam[0])

	function getMonthTSValue(monthTS: IChannelTS, fVs: string): number {
		if (!monthTS) return 0;
	
		switch (fVs) {
			case "BP":
				return monthTS.planBusinessPlan;
			case "SRE":
				return monthTS.planSRE;
			default:
				return monthTS.planFRE;
		}
	}
	

	const indexLatam = majors.findIndex(item => item.code === 'LATAM');
	if (indexLatam !== -1) {
	  majors.splice(indexLatam, 1);
	}

	const majorsArray = majors.map((obj: IOverviewChannel, i: number) => {
		// Reset all values for each new object
		let currentValue: number = 0;
		let valueBase: number = 0;
		let valueBaseYTD: number = 0;
		let valueLY: number = 0;
		let valueTS: number = 0;
	
		// Get current month info to calculate YTD
		let monthChild: IOverviewChannel | undefined = undefined;
		if (monthOverview) {
			monthChild = monthOverview.children.find((child: IOverviewChannel) => child.code === obj.code);
		}
	
		const fsChannels: IChannelFC[] = obj.actualChannels || [];
		const tsChannels: IChannelTS[] = obj.planChannels || [];
	
		// Ensure we're using the correct channel mapping
		tsChannels.forEach((tsChild: IChannelTS, j: number) => {
			const isRelevantChannel = fType === tsChild.name || fType === undefined;
			if (!isRelevantChannel) return;
	
			// Find matching FC channel by name instead of index
			const fcChild = fsChannels.find(fc => fc.name === tsChild.name);
			
			// Update base value
			valueBase += getChannelTS(tsChild, fVs);
	
			// Calculate values for current month
			if (monthChild && isYTD) {
				const monthFS = monthChild.actualChannels?.find(fc => fc.name === tsChild.name);
				const monthTS = monthChild.planChannels?.find(ts => ts.name === tsChild.name);
	
				currentValue += monthFS?.actual || 0;
				valueBaseYTD += monthTS ? getMonthTSValue(monthTS, fVs) : 0;
			} else {
				// Calculate value if not a specific month
				valueTS += fcChild?.actual || 0;
			}
	
			// Calculate previous year value
			if (obj.lastYear?.actualChannels?.length > 0) {
				const lyChannel = obj.lastYear.actualChannels.find(fc => fc.name === tsChild.name);
				valueLY += lyChannel?.actual || 0;
			}
		});
	
		// Combine values appropriately
		if (monthChild && isYTD) {
			valueTS += currentValue;
			valueBase += valueBaseYTD;
		}
	
		// Rest of your rendering logic remains the same

		if(obj.children && obj.code !== 'LATAM') {
			return (
				<tr key={`countryData-${obj.id}`}>
					<td>                    
						<a className="childrenButton" data-row-class={`childrenRow-${obj.id}`} onClick={toggleChildren} title="Toggle">
							{obj.name}
						</a>
					</td>
					<td>{Formatter.DisplayValue(valueTS)}</td>
					<td>{Formatter.DisplayPercent((valueTS / (valueLY) - 1) * 100)}</td>
					<td className="table__data--border">{Formatter.DisplayPercent((valueTS / (valueBase) - 1) * 100)}</td>
				</tr>
			);
		}
	
		return (
			<tr key={`countryData-${obj.id}`}>
				<td>{obj.name}</td>
				<td>{Formatter.DisplayValue(valueTS)}</td>
				<td>{Formatter.DisplayPercent((valueTS / (valueLY) - 1) * 100)}</td>
				<td className="table__data--border">{Formatter.DisplayPercent((valueTS / (valueBase) - 1) * 100)}</td>
			</tr>
		);
	});

	return (
		<React.Fragment>
			{loading || !current ? (
				<Loading />
			) : (
				<React.Fragment>
					 <section className="content__block">
						<h2 className="content__title">Plan - {current.code}</h2>
	
						<div className="select__boxes">
							<div className="content__select">
									<Select
										className="select-custom select-custom--medium select-custom--shadow"
										name="channelType"
										title="Type"
										options={channelTypes.map(mapChannelTypeList)}
										value={channelType}
										handleChange={(
											event: React.FormEvent<HTMLSelectElement>
										) => {
											setChannelType(event.currentTarget.value);
										}}
									/>
							</div>

							<div className="content__select">
								<Select
									className="select-custom select-custom--medium select-custom--shadow"
									name="month"
									title="Month"
									options={months.map(mapList)}
									value={month}
									handleChange={(
										event: React.FormEvent<HTMLSelectElement>
									) => {
										setMonth(+event.currentTarget.value);
									}}
								/>
							</div>

							<div className="content__select">
								<Select
									className="select-custom select-custom--medium select-custom--shadow"
									name="year"
									title="Year"
									options={years.map((obj) => { return { value: obj.name, label: obj.name } })}
									value={year}
									handleChange={(
										event: React.FormEvent<HTMLSelectElement>
									) => {
										setYear(+event.currentTarget.value);
									}}
								/>
							</div>

							<div className="content__select">
								<Select
									className="select-custom select-custom--medium select-custom--shadow"
									name=""
									title={handleChannelText(channelType, true)}
									options={[{ value: "", label: "Total" }].concat(
										current.planChannels.map((obj: IChannelTS) => {
											return { value: obj.name, label: obj.name };
										})
									)}
									value={fType}
									handleChange={(
										event: React.FormEvent<HTMLSelectElement>
									) => {
										setFType(event.currentTarget.value === "" ? undefined : event.currentTarget.value);
									}}
								/>
							</div>	

							<div className="content__select">
								<Select
										title="Period"
										name="fPs"
										className="select-custom select-custom--big select-custom--shadow"
										options={[
											{ value: "MONTH", label: "Month" },
											{ value: "YTD", label: "YTD" }
										]}
										value={fPs}
										handleChange={(
											event: React.FormEvent<HTMLSelectElement>
										) => {
											setFPs(event.currentTarget.value);
										}}
									/>
							</div>

							<div className="content__select">
								<Select
										name="fVs"
										title="Vs"
										className="select-custom select-custom--big select-custom--shadow"
										options={[
											...getTSTypes(),
										]}
										value={fVs}
										handleChange={(
											event: React.FormEvent<HTMLSelectElement>
										) => {
											setFVs(event.currentTarget.value);
										}}
									/>
							</div>
						</div>
					</section>
					

					<section className="content__block">
						<div className="column">
							<div className="col--large">
								<div className="table">
									<table className="table__data">
										<tbody>
											<tr>
												<th>COUNTRIES</th>
												<th>ACTUAL</th>
												<th>VS. LY</th>
												<th>VS. {fVs}</th>
											</tr>
											{majorsArray}
											<tr></tr>
											{allArray}

										</tbody>
									</table>
								</div>
							</div>

							<div className="col--large">
								<div className="waterfall">
									<Waterfall id="chartS1div" data={graphData} />
								</div>
								<div className="donuts">
									{current.actualChannels.map(
										(obj: IChannelFC, i: number) => {
											const data: any = [];
											
											majors.forEach((children, j) => {
												const channel: IChannelFC = children.actualChannels[i];

												let monthChild: IOverviewChannel | undefined = undefined;
												let ts = 0;

												if (monthOverview) {
													monthChild = monthOverview.children.find((child: IOverviewChannel) => child.code === children.code);
												}

												if (monthChild && fPs !== 'MONTH') {
													let monthFS = monthChild.actualChannels[i];

													let currentValue = monthFS?.actual ?? 0;

													ts += currentValue;
												} else {
													ts += channel?.actual ?? 0
												}
												
												data.push({
													country: children.name,
													value: ts,
													color: am4core.color(graphColors[j])
												});
											});

											return (
												<div
													key={"donutGraph" + obj.name + i}
													className="donut__box"
												>
													<h3 className="donut__title">{obj.name}</h3>
													<Donut id={obj.name} data={data} colors={graphColors}/>
												</div>
											);
										}
									)}
									<div className="donut__box donut__box--sidebyside">
										<div className="donut__legend donut__box--sidebyside">
											{majors.map(
												(obj: IOverviewChannel, i: number) => {

													return (
														<p
															key={"donutLegend" + obj.name + obj.id}
															className="donut__country"
														>
															<span
																className="donut__legend--dot"
																style={{ backgroundColor: graphColors[i] }}
															></span>
															{obj.name}
														</p>
													);
												}
											)}
										</div>
									</div>
								</div>
							</div>
						</div>
					</section>

					{/* <section className="content__block">
						<div className="content__select">
							<Select
								name="tstype2"
								title="Reference"
								className="select-custom select-custom--small"
								options={getTSTypes()}
								value={tstype2}
								handleChange={(event: React.FormEvent<HTMLSelectElement>) => {
									setType2(event.currentTarget.value);
								}}
							/>
						</div>
						<div className="table">
							<table className="table__data">
								<tbody>
									<tr>
										<th>COUNTRIES</th>
										{current.planChannels.map((obj: IChannelTS) => {
											return (
												<React.Fragment key={obj.name}>
													<th>
														ACTUAL <br /> {obj.name}
													</th>
													<th>VS. {tstype2}</th>
												</React.Fragment>
											);
										})}
									</tr>
									{majors.map(mapChannels)}
									<tr></tr>
									{all.map(mapChannels)}
								</tbody>
							</table>
						</div>
					</section> */}


				</React.Fragment>
			)}
		</React.Fragment>
	);
};

export default ChannelOverview;
